'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import { createClient } from '@/lib/supabase/client';
import {
  User,
  Mail,
  Phone,
  Calendar,
  MapPin,
  BookOpen,
  FileText,
  CheckCircle,
  ArrowRight,
  ChevronRight,
} from 'lucide-react';

interface FormData {
  fullName: string;
  email: string;
  phone: string;
  dateOfBirth: string;
  address: string;
  program: string;
  motivation: string;
}

const PROGRAMS = [
  'Python Programming',
  'Statistics',
  'Philosophy of Mind',
];

export default function RegisterPage() {
  const [step, setStep] = useState<1 | 2>(1);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState<FormData>({
    fullName: '',
    email: '',
    phone: '',
    dateOfBirth: '',
    address: '',
    program: '',
    motivation: '',
  });

  const supabase = createClient();

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    setError('');
  };

  const validateStep1 = () => {
    if (!form.fullName.trim()) return 'Full name is required.';
    if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      return 'A valid email address is required.';
    if (!form.phone.trim()) return 'Phone number is required.';
    if (!form.dateOfBirth) return 'Date of birth is required.';
    return '';
  };

  const validateStep2 = () => {
    if (!form.program) return 'Please select a program.';
    if (!form.motivation.trim() || form.motivation.trim().length < 50)
      return 'Please write at least 50 characters about your motivation.';
    return '';
  };

  const handleNext = () => {
    const err = validateStep1();
    if (err) { setError(err); return; }
    setStep(2);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const err = validateStep2();
    if (err) { setError(err); return; }

    setLoading(true);
    setError('');

    try {
      const { error: dbError } = await supabase.from('student_registrations').insert({
        full_name: form.fullName.trim(),
        email: form.email.trim().toLowerCase(),
        phone: form.phone.trim(),
        date_of_birth: form.dateOfBirth || null,
        address: form.address.trim() || null,
        program: form.program,
        motivation: form.motivation.trim(),
        status: 'pending',
      });

      if (dbError) {
        if (dbError.code === '23505') {
          setError('An application with this email already exists. Please contact support if you need assistance.');
        } else {
          setError(dbError.message);
        }
        return;
      }

      setSubmitted(true);
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-amber-50 to-orange-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl border border-orange-100 max-w-md w-full p-10 text-center">
          <div className="w-20 h-20 bg-emerald-50 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-emerald-500" />
          </div>
          <h2 className="text-2xl font-bold text-stone-900 mb-3">Application Submitted</h2>
          <p className="text-stone-500 text-sm leading-relaxed mb-6">
            Thank you, <span className="font-semibold text-stone-700">{form.fullName}</span>. Your application has been received and is under review.
            Once approved, you will receive your login credentials at{' '}
            <span className="font-semibold text-orange-600">{form.email}</span>.
          </p>
          <div className="bg-stone-50 rounded-xl p-4 mb-6 text-left space-y-2">
            <p className="text-xs font-semibold text-stone-500 uppercase tracking-wider mb-3">What happens next?</p>
            {[
              'Our admissions team reviews your application',
              'You receive an email with your Student ID and temporary password',
              'Log in and complete your profile on first sign-in',
            ].map((s, i) => (
              <div key={i} className="flex items-start gap-3">
                <span className="w-5 h-5 rounded-full bg-orange-100 text-orange-600 text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">
                  {i + 1}
                </span>
                <p className="text-sm text-stone-600">{s}</p>
              </div>
            ))}
          </div>
          <Link
            href="/login"
            className="inline-flex items-center gap-2 text-sm font-medium text-orange-600 hover:text-orange-700 transition-colors"
          >
            Go to Login <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-amber-50 to-orange-100">
      {/* Header */}
      <header className="bg-white border-b border-orange-100 px-6 py-4">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <Link href="/" className="flex items-center gap-2.5">
            <AppLogo size={32} />
            <span className="font-extrabold text-lg tracking-tight text-stone-900">iBrain Academy</span>
          </Link>
          <Link
            href="/login"
            className="text-sm font-medium text-stone-500 hover:text-stone-800 transition-colors"
          >
            Already have an account? <span className="text-orange-600 font-semibold">Sign in</span>
          </Link>
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-4 py-12">
        {/* Title */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 bg-orange-50 text-orange-700 text-xs font-semibold px-3 py-1.5 rounded-full mb-4 border border-orange-200">
            <BookOpen className="w-3.5 h-3.5" />
            Student Enrollment
          </div>
          <h1 className="text-3xl font-extrabold text-stone-900 mb-3">Apply to iBrain Academy</h1>
          <p className="text-stone-500 text-sm max-w-md mx-auto">
            Complete the application form below. Our admissions team will review your application and send your credentials upon approval.
          </p>
        </div>

        {/* Step indicator */}
        <div className="flex items-center justify-center gap-3 mb-8">
          {[
            { num: 1, label: 'Personal Info' },
            { num: 2, label: 'Program & Statement' },
          ].map((s, i) => (
            <React.Fragment key={s.num}>
              <div className="flex items-center gap-2">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all ${
                    step === s.num
                      ? 'bg-orange-500 text-white shadow-md shadow-orange-200'
                      : step > s.num
                      ? 'bg-emerald-500 text-white' :'bg-stone-100 text-stone-400'
                  }`}
                >
                  {step > s.num ? <CheckCircle className="w-4 h-4" /> : s.num}
                </div>
                <span
                  className={`text-sm font-medium hidden sm:block ${
                    step === s.num ? 'text-stone-900' : 'text-stone-400'
                  }`}
                >
                  {s.label}
                </span>
              </div>
              {i < 1 && <ChevronRight className="w-4 h-4 text-stone-300" />}
            </React.Fragment>
          ))}
        </div>

        {/* Form Card */}
        <div className="bg-white rounded-2xl shadow-lg border border-orange-100 overflow-hidden">
          <div className="bg-gradient-to-r from-orange-500 to-orange-600 px-8 py-5">
            <h2 className="text-white font-semibold text-base">
              {step === 1 ? 'Personal Information' : 'Program & Personal Statement'}
            </h2>
            <p className="text-orange-100 text-xs mt-0.5">
              {step === 1 ? 'Tell us about yourself' : 'Choose your program and share your goals'}
            </p>
          </div>

          <form onSubmit={step === 1 ? (e) => { e.preventDefault(); handleNext(); } : handleSubmit} className="p-8 space-y-5">
            {step === 1 && (
              <>
                <div>
                  <label className="block text-xs font-semibold text-stone-600 mb-1.5 uppercase tracking-wide">
                    Full Name <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <User className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                    <input
                      type="text"
                      name="fullName"
                      value={form.fullName}
                      onChange={handleChange}
                      placeholder="e.g. Ahmed Al-Rashid"
                      className="w-full pl-10 pr-4 py-3 border border-stone-200 rounded-xl text-sm text-stone-900 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-stone-600 mb-1.5 uppercase tracking-wide">
                    Email Address <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                    <input
                      type="email"
                      name="email"
                      value={form.email}
                      onChange={handleChange}
                      placeholder="your@email.com"
                      className="w-full pl-10 pr-4 py-3 border border-stone-200 rounded-xl text-sm text-stone-900 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-xs font-semibold text-stone-600 mb-1.5 uppercase tracking-wide">
                      Phone Number <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <Phone className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                      <input
                        type="tel"
                        name="phone"
                        value={form.phone}
                        onChange={handleChange}
                        placeholder="+971 50 000 0000"
                        className="w-full pl-10 pr-4 py-3 border border-stone-200 rounded-xl text-sm text-stone-900 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-stone-600 mb-1.5 uppercase tracking-wide">
                      Date of Birth <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <Calendar className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                      <input
                        type="date"
                        name="dateOfBirth"
                        value={form.dateOfBirth}
                        onChange={handleChange}
                        className="w-full pl-10 pr-4 py-3 border border-stone-200 rounded-xl text-sm text-stone-900 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-stone-600 mb-1.5 uppercase tracking-wide">
                    Address <span className="text-stone-400 font-normal normal-case">(optional)</span>
                  </label>
                  <div className="relative">
                    <MapPin className="absolute left-3.5 top-3.5 w-4 h-4 text-stone-400" />
                    <input
                      type="text"
                      name="address"
                      value={form.address}
                      onChange={handleChange}
                      placeholder="City, Country"
                      className="w-full pl-10 pr-4 py-3 border border-stone-200 rounded-xl text-sm text-stone-900 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                    />
                  </div>
                </div>
              </>
            )}

            {step === 2 && (
              <>
                <div>
                  <label className="block text-xs font-semibold text-stone-600 mb-1.5 uppercase tracking-wide">
                    Program of Interest <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <BookOpen className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400 pointer-events-none" />
                    <select
                      name="program"
                      value={form.program}
                      onChange={handleChange}
                      className="w-full pl-10 pr-4 py-3 border border-stone-200 rounded-xl text-sm text-stone-900 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all appearance-none bg-white"
                    >
                      <option value="">Select a program...</option>
                      {PROGRAMS.map((p) => (
                        <option key={p} value={p}>{p}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-stone-600 mb-1.5 uppercase tracking-wide">
                    Personal Statement <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <FileText className="absolute left-3.5 top-3.5 w-4 h-4 text-stone-400" />
                    <textarea
                      name="motivation"
                      value={form.motivation}
                      onChange={handleChange}
                      rows={5}
                      placeholder="Tell us why you want to join iBrain Academy and what you hope to achieve (minimum 50 characters)..."
                      className="w-full pl-10 pr-4 py-3 border border-stone-200 rounded-xl text-sm text-stone-900 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all resize-none"
                    />
                  </div>
                  <p className="text-xs text-stone-400 mt-1.5 text-right">
                    {form.motivation.length} / 50 min characters
                  </p>
                </div>

                <div className="bg-amber-50 border border-amber-100 rounded-xl p-4">
                  <p className="text-xs text-amber-700 leading-relaxed">
                    <span className="font-semibold">Note:</span> By submitting this application, you confirm that all information provided is accurate. Your application will be reviewed by our admissions team within 2–5 business days.
                  </p>
                </div>
              </>
            )}

            {error && (
              <div className="bg-red-50 border border-red-100 rounded-xl px-4 py-3">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            <div className="flex items-center gap-3 pt-2">
              {step === 2 && (
                <button
                  type="button"
                  onClick={() => { setStep(1); setError(''); }}
                  className="flex-1 py-3 px-6 border border-stone-200 rounded-xl text-sm font-semibold text-stone-600 hover:bg-stone-50 transition-all"
                >
                  Back
                </button>
              )}
              <button
                type="submit"
                disabled={loading}
                className="flex-1 py-3 px-6 bg-orange-500 hover:bg-orange-600 disabled:opacity-60 disabled:cursor-not-allowed text-white rounded-xl text-sm font-semibold transition-all shadow-md shadow-orange-200 flex items-center justify-center gap-2"
              >
                {loading ? (
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : step === 1 ? (
                  <>Continue <ArrowRight className="w-4 h-4" /></>
                ) : (
                  <>Submit Application <CheckCircle className="w-4 h-4" /></>
                )}
              </button>
            </div>
          </form>
        </div>

        <p className="text-center text-xs text-stone-400 mt-6">
          Already enrolled?{' '}
          <Link href="/login" className="text-orange-600 font-medium hover:underline">
            Sign in to your account
          </Link>
        </p>
      </div>
    </div>
  );
}
